import { app } from "@microsoft/teams-js";
import type { FailedReason } from "@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/appInitialization.js";
import { useCallback, useContext } from "react";

import {
  HostApplication,
  Platform,
  TeamsClientVariant,
} from "./detect-platform.js";
import { PlatformContext } from "./platform.context.js";

export function usePlatform() {
  const context = useContext(PlatformContext);

  if (!context.__ctx_init) {
    throw new Error("usePlatform has to be used within a PlatformProvider");
  }

  const getPlatformContext = useCallback(() => {
    if (context.platform !== Platform.Microsoft) {
      return null;
    }

    return app.getContext();
  }, [context.platform]);

  const notifyAppLoaded = useCallback(() => {
    if (context.platform === Platform.Microsoft) {
      app.notifyAppLoaded();
      app.notifySuccess();
    }
  }, [context.platform]);

  const notifyAppFailure = useCallback(
    (reason: FailedReason, message: string) => {
      if (context.platform === Platform.Microsoft) {
        app.notifyFailure({
          message,
          reason,
        });
      }
    },
    [context.platform]
  );

  const safeOperation = useCallback(
    async <ResultMicrosoft, ResultWeb>(
      operation: () => ResultMicrosoft | Promise<ResultMicrosoft>,
      fallback: () => ResultWeb | Promise<ResultWeb>
    ) => {
      if (context.platform !== Platform.Microsoft) {
        return await fallback();
      }

      try {
        return await operation();
      } catch {
        return await fallback();
      }
    },
    [context.platform]
  );

  const isTeamsDesktopClient = useCallback(
    () =>
      context.platform === Platform.Microsoft &&
      context.hostApplication === HostApplication.Teams &&
      context.teamsClientVariant === TeamsClientVariant.Desktop,
    [context.platform, context.hostApplication, context.teamsClientVariant]
  );

  const isTeamsWebClient = useCallback(
    () =>
      context.platform === Platform.Microsoft &&
      context.hostApplication === HostApplication.Teams &&
      context.teamsClientVariant === TeamsClientVariant.Web,
    [context.platform, context.hostApplication, context.teamsClientVariant]
  );

  const isM365OrOutlookWebClient = useCallback(
    () =>
      context.platform === Platform.Microsoft &&
      (context.hostApplication === HostApplication.Office ||
        context.hostApplication === HostApplication.Outlook) &&
      context.teamsClientVariant === TeamsClientVariant.Web,
    [context.platform, context.hostApplication, context.teamsClientVariant]
  );

  const isBezonedWebApp = useCallback(
    () => context.platform === Platform.Web,
    [context.platform]
  );

  /**
   * Check whether the user is using mobile app to open BeZoned - this may be Teams Mobile,
   * Outlook Mobile or M365 Mobile.
   */
  const isMobileClient = useCallback(
    () =>
      context.platform === Platform.Microsoft &&
      context.teamsClientVariant === TeamsClientVariant.Mobile,
    [context.platform, context.teamsClientVariant]
  );

  return {
    getPlatformContext,
    hostApplication: context.hostApplication,
    isBezonedWebApp,
    isM365OrOutlookWebClient,
    isMobileClient,
    isTeamsDesktopClient,
    isTeamsWebClient,
    notifyAppFailure,
    notifyAppLoaded,
    platform: context.platform,
    safeOperation,
    teamsClientVariant: context.teamsClientVariant,
  };
}
